import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Market, Salary } from "@/hooks/useAdminPayroll";
import { PayrollPosition, ServiceStyle } from "@/types/payroll-admin";

interface SalariesTabProps {
  positions: PayrollPosition[];
  markets: Market[];
  salaries: Salary[];
  onAddMarket: (
    market: Omit<Market, "id" | "created_at" | "updated_at">
  ) => void;
  onUpdateMarket: (id: string, updates: Partial<Market>) => void;
  onDeleteMarket: (id: string) => void;
  onUpsertSalary: (
    positionId: string,
    marketId: string,
    salaryData: {
      salary_efficient: number;
      salary_balanced: number;
      salary_luxury: number;
    }
  ) => void;
  getSalaryEntry: (positionId: string, marketId: string) => Salary | undefined;
}

const emptyMarketForm = {
  name: "",
  country: "",
  flag_emoji: "",
  salary_multiplier: 1,
  work_week_hours: 40,
  annual_leave_days: 25,
  social_charges_percent: 30,
};

const emptySalaryForm = {
  salary_efficient: 0,
  salary_balanced: 0,
  salary_luxury: 0,
};

export function SalariesTab({
  positions,
  markets,
  salaries,
  onAddMarket,
  onUpdateMarket,
  onDeleteMarket,
  onUpsertSalary,
  getSalaryEntry,
}: SalariesTabProps) {
  const [selectedMarket, setSelectedMarket] = useState(markets[0]?.id || "");
  const [serviceStyle, setServiceStyle] = useState<ServiceStyle>("balanced");
  const [isMarketDialogOpen, setIsMarketDialogOpen] = useState(false);
  const [isSalaryDialogOpen, setIsSalaryDialogOpen] = useState(false);

  const [marketFormData, setMarketFormData] = useState(emptyMarketForm);
  const [salaryFormData, setSalaryFormData] = useState(emptySalaryForm);

  const [editingMarket, setEditingMarket] = useState<Market | null>(null);
  const [editingPosition, setEditingPosition] =
    useState<PayrollPosition | null>(null);

  const market = markets.find((m) => m.id === selectedMarket);

  const openAddMarketDialog = () => {
    setMarketFormData(emptyMarketForm);
    setEditingMarket(null);
    setIsMarketDialogOpen(true);
  };

  const openEditMarketDialog = (marketToEdit: Market) => {
    setMarketFormData({
      name: marketToEdit.name,
      country: marketToEdit.country,
      flag_emoji: marketToEdit.flag_emoji,
      salary_multiplier: marketToEdit.salary_multiplier,
      work_week_hours: marketToEdit.work_week_hours,
      annual_leave_days: marketToEdit.annual_leave_days,
      social_charges_percent: marketToEdit.social_charges_percent,
    });
    setEditingMarket(marketToEdit);
    setIsMarketDialogOpen(true);
  };

  const handleMarketSubmit = () => {
    if (!marketFormData.name || !marketFormData.country) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (editingMarket) {
      onUpdateMarket(editingMarket.id, marketFormData);
      toast.success("Market updated");
    } else {
      onAddMarket({
        ...marketFormData,
        is_active: true,
      });
      toast.success("Market added");
    }

    setMarketFormData(emptyMarketForm);
    setEditingMarket(null);
    setIsMarketDialogOpen(false);
  };

  const handleMarketChange = (
    field: keyof typeof marketFormData,
    value: string | number
  ) => {
    setMarketFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const openEditSalaryDialog = (position: PayrollPosition) => {
    const salaryEntry = getSalaryEntry(position.id, selectedMarket);
    if (salaryEntry) {
      setSalaryFormData({
        salary_efficient: salaryEntry.salary_efficient,
        salary_balanced: salaryEntry.salary_balanced,
        salary_luxury: salaryEntry.salary_luxury,
      });
    } else {
      setSalaryFormData(emptySalaryForm);
    }
    setEditingPosition(position);
    setIsSalaryDialogOpen(true);
  };

  const handleSaveSalary = () => {
    if (!editingPosition || !selectedMarket) return;

    onUpsertSalary(editingPosition.id, selectedMarket, salaryFormData);

    setSalaryFormData(emptySalaryForm);
    setEditingPosition(null);
    setIsSalaryDialogOpen(false);
  };

  const handleSalaryChange = (
    field: keyof typeof salaryFormData,
    value: number
  ) => {
    setSalaryFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const renderMarketForm = () => (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="market-name">City/Region</Label>
          <Input
            id="market-name"
            value={marketFormData.name}
            onChange={(e) => handleMarketChange("name", e.target.value)}
            placeholder="e.g., Amsterdam"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="market-country">Country</Label>
          <Input
            id="market-country"
            value={marketFormData.country}
            onChange={(e) => handleMarketChange("country", e.target.value)}
            placeholder="e.g., Netherlands"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="market-flag">Flag Emoji</Label>
          <Input
            id="market-flag"
            value={marketFormData.flag_emoji}
            onChange={(e) => handleMarketChange("flag_emoji", e.target.value)}
            placeholder="🇳🇱"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="market-multiplier">Salary Multiplier</Label>
          <Input
            id="market-multiplier"
            type="number"
            step="0.05"
            value={marketFormData.salary_multiplier}
            onChange={(e) =>
              handleMarketChange("salary_multiplier", Number(e.target.value))
            }
          />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="market-workWeek">Work Week (hrs)</Label>
          <Input
            id="market-workWeek"
            type="number"
            value={marketFormData.work_week_hours}
            onChange={(e) =>
              handleMarketChange("work_week_hours", Number(e.target.value))
            }
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="market-leave">Annual Leave</Label>
          <Input
            id="market-leave"
            type="number"
            value={marketFormData.annual_leave_days}
            onChange={(e) =>
              handleMarketChange("annual_leave_days", Number(e.target.value))
            }
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="market-charges">Social Charges %</Label>
          <Input
            id="market-charges"
            type="number"
            value={marketFormData.social_charges_percent}
            onChange={(e) =>
              handleMarketChange(
                "social_charges_percent",
                Number(e.target.value)
              )
            }
          />
        </div>
      </div>
    </>
  );

  const renderSalaryForm = () => (
    <>
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Efficient (€/month)</Label>
          <Input
            type="number"
            value={salaryFormData.salary_efficient}
            onChange={(e) =>
              handleSalaryChange("salary_efficient", Number(e.target.value))
            }
          />
        </div>
        <div className="space-y-2">
          <Label>Balanced (€/month)</Label>
          <Input
            type="number"
            value={salaryFormData.salary_balanced}
            onChange={(e) =>
              handleSalaryChange("salary_balanced", Number(e.target.value))
            }
          />
        </div>
        <div className="space-y-2">
          <Label>Luxury (€/month)</Label>
          <Input
            type="number"
            value={salaryFormData.salary_luxury}
            onChange={(e) =>
              handleSalaryChange("salary_luxury", Number(e.target.value))
            }
          />
        </div>
      </div>
    </>
  );

  const getSalaryForDisplay = (positionId: string): number => {
    const salaryEntry = getSalaryEntry(positionId, selectedMarket);
    if (!salaryEntry) return 0;

    switch (serviceStyle) {
      case "efficient":
        return salaryEntry.salary_efficient;
      case "luxury":
        return salaryEntry.salary_luxury;
      default:
        return salaryEntry.salary_balanced;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Market Salaries</h2>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label>Market:</Label>
            <Select value={selectedMarket} onValueChange={setSelectedMarket}>
              <SelectTrigger className="w-[180px]">
                <div className="flex items-center gap-2 truncate">
                  {market ? (
                    <>
                      <span>{market.flag_emoji}</span>
                      <span className="truncate">{market.name}</span>
                    </>
                  ) : (
                    <span className="text-muted-foreground">Select market</span>
                  )}
                </div>
              </SelectTrigger>
              <SelectContent>
                {markets
                  .filter((m) => m.is_active)
                  .map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      <div className="flex items-center gap-2">
                        <span>{m.flag_emoji}</span>
                        <span>{m.name}</span>
                      </div>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label>Style:</Label>
            <Select
              value={serviceStyle}
              onValueChange={(v) => setServiceStyle(v as ServiceStyle)}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="efficient">Efficient</SelectItem>
                <SelectItem value="balanced">Balanced</SelectItem>
                <SelectItem value="luxury">Luxury</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            className="bg-primary-blue text-white"
            onClick={openAddMarketDialog}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Market
          </Button>
        </div>
      </div>

      {market ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {market.flag_emoji} {market.name}, {market.country}
                <Badge variant="outline">×{market.salary_multiplier}</Badge>
              </CardTitle>
              <CardDescription>
                {market.work_week_hours}h work week • {market.annual_leave_days}{" "}
                days leave • {market.social_charges_percent}% social charges
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Position</TableHead>
                    <TableHead className="text-right">
                      Monthly Salary (€)
                    </TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {positions
                    .filter((p) => p.is_active)
                    .map((position) => (
                      <TableRow key={position.id}>
                        <TableCell className="font-medium">
                          {position.name}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          €{getSalaryForDisplay(position.id).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditSalaryDialog(position)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => openEditMarketDialog(market)}
            >
              <Pencil className="h-3 w-3 mr-1" />
              Edit Market Details
            </Button>
          </div>
        </>
      ) : (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">
              No market selected or no active markets available.
            </p>
            <Button
              className="mt-4 bg-primary-blue text-white"
              onClick={openAddMarketDialog}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Market
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={isMarketDialogOpen} onOpenChange={setIsMarketDialogOpen}>
        <DialogContent className="p-0 max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex-1 overflow-y-auto p-6">
            <DialogHeader className="pb-4">
              <DialogTitle>
                {editingMarket ? "Edit Market" : "Add New Market"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">{renderMarketForm()}</div>
          </div>

          <div className="p-6 shrink-0">
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsMarketDialogOpen(false);
                  setMarketFormData(emptyMarketForm);
                  setEditingMarket(null);
                }}
              >
                Cancel
              </Button>
              <Button
                className="bg-primary-blue text-white"
                onClick={handleMarketSubmit}
              >
                {editingMarket ? "Save" : "Add Market"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isSalaryDialogOpen} onOpenChange={setIsSalaryDialogOpen}>
        <DialogContent className="p-0 max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex-1 overflow-y-auto p-6">
            <DialogHeader className="pb-4">
              <DialogTitle>Edit Salary - {editingPosition?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">{renderSalaryForm()}</div>
          </div>

          <div className="p-6 shrink-0">
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsSalaryDialogOpen(false);
                  setSalaryFormData(emptySalaryForm);
                  setEditingPosition(null);
                }}
              >
                Cancel
              </Button>
              <Button
                className="bg-primary-blue text-white"
                onClick={handleSaveSalary}
              >
                Save
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
